import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutStats } from '@/mocks/anime';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 奶泡动漫 | 品牌介绍与联系方式';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        '了解奶泡动漫品牌故事与团队理念。奶泡动漫专注高清动漫在线观看，收录12000+部番剧，日活跃用户50万+，累计播放10亿+，致力于为动漫爱好者提供极致追番体验。'
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.origin + '/about');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if (honeypot) formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9sn1b94ne17ei0sdk70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.includes('spam')) {
        setFormStatus('error');
        setFormError('提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '奶泡动漫',
        url: window.location.origin,
        description: '专注于高清动漫在线观看的平台',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@naipaoanime.com',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 奶泡动漫',
        url: window.location.origin + '/about',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin + '/' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <section className="text-center mb-14">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于 奶泡动漫
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              为每一位动漫爱好者打造最极致的追番体验
            </p>
          </section>

          <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
            {aboutStats.map((stat) => (
              <div
                key={stat.label}
                className="text-center p-6 rounded-lg bg-background-100"
              >
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">
                品牌故事
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  奶泡动漫诞生于一群资深动漫爱好者的共同梦想。我们深知每一位动漫迷在追番路上的痛点——画质模糊、更新延迟、广告满天飞、资源不全……
                </p>
                <p>
                  因此，我们决定打造一个纯粹的动漫观看平台。从最初的几十部番剧起步，到如今收录超过
                  <strong className="text-primary-500">12,000部</strong>
                  动漫作品，奶泡动漫始终坚持「高清流畅、更新及时、完全免费」的核心理念。
                </p>
                <p>
                  我们的团队由资深动漫编辑、技术开发者和内容运营组成，每一天都在努力为你带来最新、最全、最清晰的动漫观看体验。无论是深夜追番还是周末补番，奶泡动漫始终陪伴在每一位动漫爱好者身边。
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">
                我们的承诺
              </h2>
              <div className="space-y-3">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '高清画质',
                    desc: '全站1080P高清，热门番剧更支持4K超清播放，每一帧都清晰可见。',
                  },
                  {
                    icon: 'ri-flashlight-line',
                    title: '极速更新',
                    desc: '新番播出后数小时内同步更新，让你第一时间追上最新剧情。',
                  },
                  {
                    icon: 'ri-emotion-happy-line',
                    title: '完全免费',
                    desc: '无需注册，无需付费，打开即看。让每一位动漫爱好者都能无障碍追番。',
                  },
                  {
                    icon: 'ri-device-line',
                    title: '多端适配',
                    desc: '电脑、平板、手机全平台支持，随时随地畅享动漫世界。',
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex gap-4 p-4 rounded-lg bg-background-100"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  name: '星辰大海',
                  rating: 5,
                  text: '找了好久终于找到一个纯净的动漫网站！没有广告，画质真的超清晰，更新也很快，已经推荐给所有追番的朋友了。',
                },
                {
                  name: '樱花飘落',
                  rating: 5,
                  text: '界面简洁好看，分类很全，国漫日漫都能找到。最爱的芙莉莲和咒术回战都有1080P，追番体验太棒了！',
                },
                {
                  name: '二次元居民',
                  rating: 5,
                  text: '作为一个重度追番党，奶泡动漫的番剧库真的很全。每周更新通知也很及时，已经离不开这个平台了。',
                },
                {
                  name: '月光下的猫',
                  rating: 5,
                  text: '手机端体验也很好，在地铁上追番画质依然清晰流畅。感谢你们做出这么用心的动漫平台！',
                },
                {
                  name: '风之旅行者',
                  rating: 5,
                  text: '从第一季就开始用的老用户了，见证了平台从几十部到上万部的成长。初心不改，始终免费，真的难得。',
                },
                {
                  name: '草莓味夏天',
                  rating: 4,
                  text: '整体非常好，番剧资源很全。期待后续能加上弹幕功能和追番提醒，那样就更完美了！',
                },
              ].map((review, index) => (
                <div key={index} className="p-5 rounded-lg bg-background-100">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="max-w-xl mx-auto">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议？欢迎给我们留言，我们会尽快回复。
              </p>

              {formStatus === 'success' ? (
                <div className="text-center p-8 rounded-lg bg-secondary-100">
                  <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-500">
                    <i className="ri-check-line text-2xl text-background-50"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">提交成功！</h3>
                  <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复你。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="bot-field"
                  />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      你的名字
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入你的名字"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入你的留言（最多500字）"
                    ></textarea>
                  </div>
                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                      {formError}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}